/**
 * The trip a signed-in customer's Home page shows under its hero, the way
 * airline apps open on your next flight (src/components/flight/TripNowCard.jsx): a trip
 * whose first flight leaves within TRIP_NOW_LEAD_MS, or one under way, until
 * TRIP_NOW_AFTER_MS after its last flight lands.
 *
 * A booking's departing_at is the airline's local clock with no zone, so the
 * airport's zone turns it into an instant (server/airport-timezones.js, the
 * table live status uses too). An airport we don't know falls back to reading
 * the clock as UTC: hours out at worst, fine for a window measured in days,
 * and `exact` is false so nothing counts down from it.
 */
import { parseAirlineLocalTime, timezoneForAirport } from '../../server/airport-timezones.js';

const HOUR = 3600000;

/** A trip shows on Home from three days before its first flight... */
export const TRIP_NOW_LEAD_MS = 72 * HOUR;
/** ...until six hours after its last flight lands. */
export const TRIP_NOW_AFTER_MS = 6 * HOUR;

/** Trips that are going ahead (the same set live status asks about, src/hooks/useFlightLive.js). */
const GOING_AHEAD = new Set(['confirmed', 'completed', 'delinquent']);

const iataOf = (place) => {
  const code = String((typeof place === 'string' ? place : place?.iata_code || place?.IATA || place?.iataCode) || '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
};

/**
 * "AA 1219" from "AA 1219", "AA1219", "AA-01219" or "1219" with carrier AA,
 * as the server labels a flight (server/flight-status.js flightDesignator):
 * a two-character prefix with a letter in it is the carrier.
 */
function designator(carrier, rawNumber) {
  const raw = String(rawNumber ?? '').toUpperCase().replace(/[\s-]/g, '');
  if (!raw) return null;
  let code;
  let number = raw;
  const m = raw.match(/^([A-Z0-9]{2})(\d{1,6}[A-Z]?)$/);
  if (m && /[A-Z]/.test(m[1])) [, code, number] = m;
  else code = String(carrier?.iata_code || '').trim().toUpperCase();
  number = number.replace(/^0+(?=\d)/, '');
  return /^[A-Z0-9]{2}$/.test(code) && /^\d{1,4}[A-Z]?$/.test(number) ? `${code} ${number}` : null;
}

/** Reads a zone-less stamp as UTC; only for windows that allow for the error. */
function approxMs(stamp) {
  const m = String(stamp || '').match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (!m) return null;
  const ms = Date.parse(`${m[1]}T${m[2]}:00Z`);
  return Number.isFinite(ms) ? ms : null;
}

const statusOf = (b) => String(b?.status || b?.trip_status || '').toLowerCase();

/** The key that holds one trip's bookings together. */
export function tripKeyOf(booking) {
  return booking?.tripReference || booking?.trip_reference || booking?.bookingReference
    || booking?.booking_reference || booking?.itinerary_image_url || booking?.id || null;
}

/** Bookings that are going ahead, grouped into trips. */
export function groupTrips(bookings) {
  const trips = new Map();
  for (const booking of bookings || []) {
    if (!booking?.id || !GOING_AHEAD.has(statusOf(booking))) continue;
    const key = tripKeyOf(booking);
    if (!trips.has(key)) trips.set(key, []);
    trips.get(key).push(booking);
  }
  return [...trips.values()];
}

/**
 * Every flight in a trip, in time order. A row that starts and ends at the
 * same airport is a stopover, not a flight, so it's left out.
 */
export function tripFlights(bookings) {
  const out = [];
  for (const booking of bookings || []) {
    const fd = booking?.flightData || booking?.flight_data || {};
    const slices = Array.isArray(fd.slices) ? fd.slices : [];
    slices.forEach((slice, sliceIndex) => {
      const segments = Array.isArray(slice?.segments) ? slice.segments : [];
      segments.forEach((seg, segmentIndex) => {
        const origin = iataOf(seg?.origin);
        const destination = iataOf(seg?.destination);
        if (!origin || !destination || origin === destination) return;
        const originTz = timezoneForAirport(origin);
        const destinationTz = timezoneForAirport(destination);
        const departureExact = parseAirlineLocalTime(seg?.departing_at, originTz);
        const departureMs = departureExact ?? approxMs(seg?.departing_at);
        if (departureMs == null) return;
        const arrivalMs = parseAirlineLocalTime(seg?.arriving_at, destinationTz) ?? approxMs(seg?.arriving_at);
        out.push({
          bookingId: booking.id,
          sliceIndex,
          segmentIndex,
          label: designator(seg?.marketing_carrier, seg?.marketing_carrier_flight_number ?? seg?.flight_number)
            || designator(seg?.operating_carrier, seg?.operating_carrier_flight_number),
          carrier: seg?.marketing_carrier || seg?.operating_carrier || null,
          origin,
          destination,
          originTz,
          destinationTz,
          departingAtLocal: seg?.departing_at || null,
          arrivingAtLocal: seg?.arriving_at || null,
          departureMs,
          arrivalMs: arrivalMs ?? departureMs,
          exact: departureExact != null,
          journeyOrigin: iataOf(segments[0]?.origin),
          journeyDestination: iataOf(segments[segments.length - 1]?.destination),
        });
      });
    });
  }
  return out.sort((a, b) => a.departureMs - b.departureMs);
}

/**
 * The flight that matters now: the first one not yet at its destination.
 * Live status, where there is one, beats the schedule (a delayed flight is
 * still the one to watch after its booked arrival time; a cancelled one
 * stays on screen). Past the last flight, the last flight.
 */
export function nextFlight(flights, nowMs = Date.now(), liveStatusOf = () => null) {
  for (const flight of flights || []) {
    const status = liveStatusOf(flight);
    if (status) {
      if (!['landed', 'arrived'].includes(status.state)) return flight;
    } else if (flight.arrivalMs > nowMs) {
      return flight;
    }
  }
  return flights?.length ? flights[flights.length - 1] : null;
}

/**
 * The trip to lead with, or null:
 *   { bookings, flights, phase, next, startMs, endMs }
 * `phase` is 'upcoming' before the first flight, 'traveling' from its
 * departure until the last flight lands, then 'arrived'. A trip under way
 * comes first; otherwise the nearest in time, about to leave or just back.
 */
export function pickTripNow(bookings, nowMs = Date.now()) {
  let best = null;
  let bestDistance = Infinity;
  for (const trip of groupTrips(bookings)) {
    const flights = tripFlights(trip);
    if (!flights.length) continue;
    const startMs = flights[0].departureMs;
    const endMs = Math.max(...flights.map((f) => f.arrivalMs));
    if (nowMs < startMs - TRIP_NOW_LEAD_MS || nowMs > endMs + TRIP_NOW_AFTER_MS) continue;
    const phase = nowMs < startMs ? 'upcoming' : nowMs <= endMs ? 'traveling' : 'arrived';
    const distance = phase === 'traveling' ? 0 : phase === 'upcoming' ? startMs - nowMs : nowMs - endMs;
    if (distance < bestDistance) {
      best = { bookings: trip, flights, phase, next: nextFlight(flights, nowMs), startMs, endMs };
      bestDistance = distance;
    }
  }
  return best;
}

/** Is this flight taking the customer back to where the trip began? */
export function isFlightHome(flight, flights) {
  if (!flight || !flights?.length) return false;
  return flight.sliceIndex > 0 && flight.journeyDestination === flights[0].journeyOrigin;
}

/** "YYYY-MM-DD" for an instant in a zone (the device's when it's unknown). */
function dateIn(ms, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-CA', { ...(timeZone ? { timeZone } : {}), year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
  } catch {
    return new Date(ms).toISOString().slice(0, 10);
  }
}

/** "Today", "Tomorrow" or "Thu, Oct 16": the flight's local day, against today at its airport. */
export function departureDayLabel(flight, nowMs = Date.now()) {
  const m = String(flight?.departingAtLocal || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const dayUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const days = Math.round((dayUtc - Date.parse(`${dateIn(nowMs, flight.originTz)}T00:00:00Z`)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(dayUtc + 12 * HOUR));
}

/** "7:55 PM" from an airline-local stamp, read as written. */
export function localClock(stamp) {
  const m = String(stamp || '').match(/^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

/** "in 40 min", "in 5 hr", "in 2 days"; null once it's time, or when the airport's zone is unknown. */
export function departsIn(flight, nowMs = Date.now()) {
  if (!flight?.exact) return null;
  const minutes = Math.round((flight.departureMs - nowMs) / 60000);
  if (minutes <= 0) return null;
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `in ${hours} hr`;
  const days = Math.round(hours / 24);
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * How far along the flight is, 0 to 1, from live status: 0 until it takes
 * off, 1 once it lands.
 */
export function flightProgress(status, nowMs = Date.now()) {
  if (!status) return 0;
  if (['landed', 'arrived'].includes(status.state)) return 1;
  if (status.state !== 'departed') return 0;
  const off = Date.parse(status.details?.actual_off || status.actual_out || '');
  const on = Date.parse(status.estimated_in || status.scheduled_in || '');
  if (!Number.isFinite(off) || !Number.isFinite(on) || on <= off) return 0.5;
  return Math.min(0.97, Math.max(0.03, (nowMs - off) / (on - off)));
}
